import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ImageOff } from "lucide-react";
import { addOrUpdateCartItem, fetchCart } from "../store"; // import thunks
import "react-toastify/dist/ReactToastify.css";
import "./ProductList.css";
import ProductDetailCard from "../components/product_detail/ProductDetailCard";

const listPlaceholderIconProps = { size: 64, strokeWidth: 1.5, "aria-hidden": true };

// Modern Modal component using the design from paperbag_ecommerce.html
function ProductModal({ product, open, onClose }) {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.userId); // fetch userId from store

  if (!open || !product) return null;

  // Handler for Add to Cart (with MOQ)
  const handleAddToCart = (e) => {
    e?.stopPropagation?.();
    const moq = product.moq ? parseInt(product.moq) : 1;
    dispatch(addOrUpdateCartItem({ userId, product, quantity: moq }))
      .then(() => { if (userId) dispatch(fetchCart(userId)) }); // refresh cart after update
  };

  // Overlay click: only close if click is outside the card
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      onClose && onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <button
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1100,
            background: 'rgba(255,255,255,0.9)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            fontSize: 24,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <ProductDetailCard product={product} onAddToCart={handleAddToCart} />
      </div>
    </div>
  );
}

export default function ProductCard({ product }) {
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.userId); // fetch userId from store
  const cart = useSelector(state => state.cart.items);

  // Add to Cart handler for card button (default to 1)
  const handleAddToCart = (e) => {

    e.stopPropagation();
    const moq = product.moq ? parseInt(product.moq) : 100;
    // Check if item is already in cart
    const cartItem = cart.find(item => item.product === product._id || item.product === product.id);
    const existingQty = cartItem ? cartItem.quantity : 0;
    const totalQty = existingQty + moq;

    dispatch(addOrUpdateCartItem({ userId, product: product, quantity: totalQty }));
  };

  // Stock validation for ProductCard
  const stock = product.stock || 0;
  const moq = product.moq || 100;
  const isOutOfStock = stock === 0;
  const insufficientStock = stock < moq;
  const canAddToCart = !isOutOfStock && !insufficientStock;

  return (
    <>
      <div className="product-card" onClick={() => setModalOpen(true)} tabIndex={0} style={{ cursor: "pointer" }}>
        <div className="product-image">
          {product.images[0] ? (
            <img src={product.images[0]} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <span className="product-image-placeholder" aria-hidden>
              <ImageOff {...listPlaceholderIconProps} />
            </span>
          )}
          {/* Out of Stock Badge Overlay */}
          {isOutOfStock && (
            <div className="out-of-stock-badge">
              <span className="out-of-stock-text">OUT OF STOCK</span>
            </div>
          )}
          {/* Insufficient Stock Badge Overlay */}
          {!isOutOfStock && insufficientStock && (
            <div className="insufficient-stock-badge">
              <span className="insufficient-stock-text">Only {stock} left</span>
            </div>
          )}
        </div>
        {/* {product.badge && <div className="product-badge">{product.badge}</div>} */}
        <div className="product-info">
          <h3 className="product-title">{product.name}</h3>
          <p className="product-description">{product.description}</p>
          <div className="product-specs">
            {product.bagSize.cm && <span className="spec">{product.bagSize.cm}</span>}
            {product.material && <span className="spec">{product.material}</span>}
            {product.handleType && <span className="spec">{product.handleType}</span>}
          </div>
          <div className="product-footer">
            <span className="price">{`${product.price.currency} ${product.price.amount}` || "₹6"}</span>
            <button
              className="add-to-cart"
              onClick={handleAddToCart}
              disabled={!canAddToCart}
              title={
                isOutOfStock ? "Out of stock" :
                insufficientStock ? `Insufficient stock (MOQ: ${moq}, Available: ${stock})` :
                "Add to cart"
              }
            >
              {isOutOfStock ? "Out of Stock" : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
      <ProductModal product={product} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
